import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { toaster } from '@/shared/ui/toaster.component';
import {
  Button,
  Dialog,
  Field,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { Mail, Plus, SquarePen, Trash2 } from 'lucide-react';
import { useReducer, useRef } from 'react';
import { useSendPolicyEmail } from '../hooks/use-send-policy-email.hook';
import { normalizeEmail } from '@/shared/utils/normalize-email.util';

type AdditionalEmail = {
  id: string;
  email: string;
};

type State = {
  isEditingEmailCorrector: boolean;
  form: {
    emailCorrector: string;
    emailProponent: string;
    additionalEmails: AdditionalEmail[];
  };
};

interface SendPolicyEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  policyId: string;
  brokerEmail?: string;
  customerEmail?: string;
}

export function SendPolicyEmailDialog({
  isOpen,
  onClose,
  policyId,
  brokerEmail = '',
  customerEmail = '',
}: SendPolicyEmailDialogProps) {
  const [state, updateState] = useReducer(
    (
      state: State,
      action: Partial<State> | { form?: Partial<State['form']> } | { type: 'RESET' }
    ): State => {
      if ('type' in action && action.type === 'RESET') {
        return {
          isEditingEmailCorrector: false,
          form: {
            emailCorrector: brokerEmail,
            emailProponent: customerEmail,
            additionalEmails: [],
          },
        };
      }
      return {
        ...state,
        ...action,
        form: {
          ...state.form,
          ...('form' in action ? action.form : {}),
        },
      };
    },
    {
      isEditingEmailCorrector: false,
      form: {
        emailCorrector: brokerEmail,
        emailProponent: customerEmail,
        additionalEmails: [],
      },
    }
  );

  const { mutateAsync: sendEmail, isPending } = useSendPolicyEmail();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  function handleOpenChange(details: { open: boolean }) {
    if (!details.open) {
      onClose();
    } else {
      updateState({ type: 'RESET' });
    }
  }

  function handleAddEmail() {
    const newEmail: AdditionalEmail = {
      id: `additional-${Date.now()}`,
      email: '',
    };
    updateState({
      form: {
        additionalEmails: [...state.form.additionalEmails, newEmail],
      },
    });
  }

  function handleRemoveEmail(id: string) {
    updateState({
      form: {
        additionalEmails: state.form.additionalEmails.filter(
          (e: AdditionalEmail) => e.id !== id
        ),
      },
    });
  }

  function handleUpdateAdditionalEmail(id: string, email: string) {
    updateState({
      form: {
        additionalEmails: state.form.additionalEmails.map(
          (e: AdditionalEmail) => (e.id === id ? { ...e, email } : e)
        ),
      },
    });
  }

  async function handleSendEmail() {
    const allEmails = [
      state.form.emailCorrector,
      state.form.emailProponent,
      ...state.form.additionalEmails.map((e: AdditionalEmail) => e.email),
    ];

    const emails = Array.from(
      new Set(allEmails.map((e: string) => normalizeEmail(e)).filter(Boolean))
    );

    if (emails.length === 0) {
      toaster.create({
        type: 'error',
        title: 'Erro ao enviar e-mail',
        description: 'Por favor, informe pelo menos um e-mail.',
      });
      return;
    }

    try {
      for (const email of emails) {
        await sendEmail({ policyId, email });
      }

      toaster.create({
        type: 'success',
        title: emails.length > 1 ? 'E-mails enviados com sucesso' : 'E-mail enviado com sucesso',
        description: `O PDF da apólice foi enviado para ${emails.length > 1 ? 'os e-mails informados' : 'o e-mail informado'}.`,
      });

      closeButtonRef.current?.click?.();
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao enviar e-mail',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-full max-w-lg'>
          <Dialog.Header>
            <Dialog.Title>
              <Mail size={32} className='text-alba-black-500' />
              Mandar por e-mail
            </Dialog.Title>
            <Dialog.CloseTrigger asChild>
              <Dialog.CloseButton />
            </Dialog.CloseTrigger>
          </Dialog.Header>

          <Dialog.Body>
            <Text>O PDF da apólice será gerado para envio.</Text>
            <Text>Indique os e-mails que deverão receber a apólice.</Text>

            <div className='flex flex-col gap-4 mt-3'>
              <Field.Root>
                <Field.Label>E-mail Corretor</Field.Label>
                <div className='relative'>
                  <Field.Input
                    type='text'
                    placeholder='exemplo@email.com'
                    className='pr-10'
                    value={state.form.emailCorrector}
                    onChange={e =>
                      updateState({
                        form: { emailCorrector: e.target.value.toLowerCase() },
                      })
                    }
                    disabled={!state.isEditingEmailCorrector}
                    maxLength={FIELD_MAX_LENGTHS.email}
                  />
                  <button
                    type='button'
                    onClick={() =>
                      updateState({
                        isEditingEmailCorrector: !state.isEditingEmailCorrector,
                      })
                    }
                    className='absolute right-3 top-1/2 transform -translate-y-1/2 text-alba-gray-500 hover:text-alba-gray-600 transition-colors'
                  >
                    <SquarePen className='size-5 text-alba-gray-500' />
                  </button>
                </div>
              </Field.Root>

              <Field.Root>
                <Field.Label>E-mail Proponente</Field.Label>
                <Field.Input
                  type='text'
                  placeholder='exemplo@email.com'
                  className='pr-10'
                  value={state.form.emailProponent}
                  onChange={e =>
                    updateState({
                      form: { emailProponent: e.target.value.toLowerCase() },
                    })
                  }
                  maxLength={FIELD_MAX_LENGTHS.email}
                />
              </Field.Root>

              {state.form.additionalEmails.map(
                (additionalEmail: AdditionalEmail, index: number) => (
                  <Field.Root key={additionalEmail.id}>
                    <Field.Label>E-mail Adicional {index + 1}</Field.Label>
                    <div className='relative'>
                      <Field.Input
                        type='text'
                        placeholder='exemplo@email.com'
                        className='pr-10'
                        value={additionalEmail.email}
                        onChange={e =>
                          handleUpdateAdditionalEmail(
                            additionalEmail.id,
                            e.target.value.toLowerCase()
                          )
                        }
                        maxLength={FIELD_MAX_LENGTHS.email}
                      />
                      <button
                        type='button'
                        onClick={() => handleRemoveEmail(additionalEmail.id)}
                        className='absolute right-3 top-1/2 transform -translate-y-1/2 text-alba-red-500 hover:text-alba-red-600 transition-colors'
                      >
                        <Trash2 className='size-5' />
                      </button>
                    </div>
                  </Field.Root>
                )
              )}

              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={handleAddEmail}
                className='flex items-center gap-2 self-start'
              >
                <Plus className='size-4' />
                Adicionar outro e-mail
              </Button>
            </div>
          </Dialog.Body>

          <Dialog.Footer>
            <Dialog.CloseTrigger asChild ref={closeButtonRef}>
              <Button variant='ghost' size='md'>
                Cancelar
              </Button>
            </Dialog.CloseTrigger>
            <Button
              variant='primary'
              size='md'
              disabled={isPending}
              loading={isPending}
              onClick={handleSendEmail}
            >
              Enviar
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
